import ReactECharts from "echarts-for-react";
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select, { components } from "react-select";
import AnimatedCounter from "../../../components/AnimatedCounter/AnimatedCounter";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./StatusNoleggi.css";
import Lf23Loader from "../../../components/Lf23Loader";

const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);
    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return <div className="status-noleggi-select-more-badge">+{values.length - 2}</div>;
    }
    return null;
};

const StatusNoleggi = ({ filters, loading: parentLoading = false }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // --- KPI + Chart state ---
    const [kpiData, setKpiData] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [isFullscreenChart, setIsFullscreenChart] = useState(false);

    // --- Table state ---
    const [tableData, setTableData] = useState([]);
    const [tableDataTotale, setTableDataTotale] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [isFullScreenTable, setIsFullScreenTable] = useState(false);
    const [expandedRows, setExpandedRows] = useState([]);

    // Opzioni dropdown tabella
    const colonneOptions = [
        { value: 'anno', label: 'Anno' },
        { value: 'trimestre', label: 'Trimestre' },
        { value: 'mese', label: 'Mese' },
    ];

    const righeOptions = [
        { value: 'tipologia', label: 'Tipologia' },
        { value: 'sottotipologia', label: 'Sottotipologia' },
        { value: 'targa', label: 'Targa' },
    ];

    const [selectedColonne, setSelectedColonne] = useState([]);
    const [selectedRighe, setSelectedRighe] = useState([
        { value: 'sottotipologia', label: 'Sottotipologia' },
        { value: 'targa', label: 'Targa' },
    ]);

    // ESC handler
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenChart(false);
                setIsFullScreenTable(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreenChart, isFullScreenTable]);

    const buildFilterParams = () => {
        const params = new URLSearchParams();
        if (filters?.fromDate) params.append('dataInizio', filters.fromDate);
        if (filters?.toDate) params.append('dataFine', filters.toDate);
        if (filters?.tipologiaVeicolo?.length > 0) {
            params.append('tipologia', filters.tipologiaVeicolo.map(t => t.value).join(','));
        }
        if (filters?.sottotipologia?.length > 0) {
            params.append('sottotipologia', filters.sottotipologia.map(s => s.value).join(','));
        }
        if (filters?.targa?.length > 0) {
            params.append('targa', filters.targa.map(t => t.value).join(','));
        }
        return params.toString();
    };

    const buildFilterParamsNoDate = () => {
        const params = new URLSearchParams();
        if (filters?.tipologiaVeicolo?.length > 0) {
            params.append('tipologia', filters.tipologiaVeicolo.map(t => t.value).join(','));
        }
        if (filters?.sottotipologia?.length > 0) {
            params.append('sottotipologia', filters.sottotipologia.map(s => s.value).join(','));
        }
        if (filters?.targa?.length > 0) {
            params.append('targa', filters.targa.map(t => t.value).join(','));
        }
        return params.toString();
    };

    // Fetch KPI + chart
    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            try {
                const filterParams = buildFilterParams();
                const queryString = filterParams ? `?${filterParams}` : '';
                const res = await axios.get(`${API_URL}/operation-automezzi/status-noleggi${queryString}`);
                setKpiData(res.data.kpi);
                setChartData(res.data.chart);
            } catch (error) {
                console.error("Errore nel caricamento dati Status Noleggi:", error);
            } finally {
                setDataLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters]);

    // Fetch table data
    useEffect(() => {
        const fetchTableData = async () => {
            setTableLoading(true);
            try {
                const filterParams = buildFilterParams();
                const queryString = filterParams ? `?${filterParams}` : '';
                const res = await axios.get(`${API_URL}/operation-automezzi/status-noleggi/table${queryString}`);
                setTableData(res.data);
            } catch (error) {
                console.error("Errore nel caricamento dati tabella:", error);
            } finally {
                setTableLoading(false);
            }
        };
        fetchTableData();
    }, [API_URL, filters]);

    // Fetch table data senza filtro date (per colonna Totale contratto)
    useEffect(() => {
        const fetchTableDataTotale = async () => {
            try {
                const filterParams = buildFilterParamsNoDate();
                const queryString = filterParams ? `?${filterParams}` : '';
                const res = await axios.get(`${API_URL}/operation-automezzi/status-noleggi/table${queryString}`);
                setTableDataTotale(res.data);
            } catch (error) {
                console.error("Errore nel caricamento dati totali tabella:", error);
            }
        };
        fetchTableDataTotale();
    }, [API_URL, filters]);

    const isLoading = parentLoading || dataLoading;
    const isDataReady = !parentLoading && !dataLoading && kpiData !== null && chartData.length > 0;
    const isEmpty = !parentLoading && !dataLoading && kpiData !== null && chartData.length === 0;

    // =============================================
    // TABELLA PARAMETRICA - Logica di trasformazione
    // =============================================

    const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

    const meseToTrimestreMap = { 1: 1, 2: 1, 3: 1, 4: 2, 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4, 11: 4, 12: 4 };

    const anniDisponibili = useMemo(() => {
        if (!tableData || tableData.length === 0) return [];
        return [...new Set(tableData.map(d => String(d.anno)))].sort();
    }, [tableData]);

    const mesiDisponibili = useMemo(() => {
        if (!tableData || tableData.length === 0) return {};
        const mesiPerAnno = {};
        tableData.forEach(d => {
            const anno = String(d.anno);
            const mese = Number(d.mese);
            if (!mesiPerAnno[anno]) mesiPerAnno[anno] = new Set();
            mesiPerAnno[anno].add(mese);
        });
        Object.keys(mesiPerAnno).forEach(anno => {
            mesiPerAnno[anno] = [...mesiPerAnno[anno]].sort((a, b) => a - b);
        });
        return mesiPerAnno;
    }, [tableData]);

    const isTrimestreValido = (anno, trimestre) => {
        const mesiAnno = mesiDisponibili[anno] || [];
        return mesiAnno.some(m => meseToTrimestreMap[m] === trimestre);
    };

    const isMeseValido = (anno, mese) => {
        const mesiAnno = mesiDisponibili[anno] || [];
        return mesiAnno.includes(mese);
    };

    const formatNumber = (num) => {
        if (num === 0) return '-';
        return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(num);
    };

    // Periodi da mostrare in base ai dropdown
    const getPeriodiDaMostrare = () => {
        if (selectedColonne.length === 0) {
            return [{ chiave: 'totale', etichetta: 'Totale', anno: 'totale' }];
        }

        let periodi = [];
        const haTrimestreSelezionato = selectedColonne.some(opt => opt.value === 'trimestre');
        const haMeseSelezionato = selectedColonne.some(opt => opt.value === 'mese');
        const haAnnoSelezionato = selectedColonne.some(opt => opt.value === 'anno');

        for (let i = 0; i < anniDisponibili.length; i++) {
            const anno = anniDisponibili[i];
            if (haMeseSelezionato) {
                for (let m = 1; m <= 12; m++) {
                    if (isMeseValido(anno, m)) {
                        periodi.push({ chiave: `${anno}-${String(m).padStart(2, '0')}`, etichetta: nomiMesi[m - 1], anno });
                    }
                }
            } else if (haTrimestreSelezionato) {
                for (let t = 1; t <= 4; t++) {
                    if (isTrimestreValido(anno, t)) {
                        periodi.push({ chiave: `${anno}-trim${t}`, etichetta: `T${t}`, anno });
                    }
                }
            } else if (haAnnoSelezionato) {
                periodi.push({ chiave: anno, etichetta: anno, anno });
            }
        }

        periodi.push({ chiave: 'totale', etichetta: 'Totale', anno: 'totale' });
        return periodi;
    };

    const periodiDaMostrare = getPeriodiDaMostrare();

    // Trasformazione dati: flat -> gerarchico (tipologia > sottotipologia > targa)
    const trasformaDati = (datiGrezzi) => {
        let campoLivello1 = 'tipologia';
        let campoLivello2 = null;
        let campoLivello3 = null;

        if (selectedRighe.length > 0) campoLivello1 = selectedRighe[0].value;
        if (selectedRighe.length > 1) campoLivello2 = selectedRighe[1].value;
        if (selectedRighe.length > 2) campoLivello3 = selectedRighe[2].value;

        const raggruppati = {};
        const creaPeriodoVuoto = () => ({ kmFatti: 0, kmNoleggio: 0, kmFattiContratto: 0 });

        const meseToTrim = { '01': 'trim1', '02': 'trim1', '03': 'trim1', '04': 'trim2', '05': 'trim2', '06': 'trim2', '07': 'trim3', '08': 'trim3', '09': 'trim3', '10': 'trim4', '11': 'trim4', '12': 'trim4' };

        for (let i = 0; i < datiGrezzi.length; i++) {
            const riga = datiGrezzi[i];
            const valore1 = riga[campoLivello1] || 'Non assegnato';
            const valore2 = campoLivello2 ? (riga[campoLivello2] || 'Non assegnato') : null;
            const valore3 = campoLivello3 ? (riga[campoLivello3] || 'Non assegnato') : null;

            const kmFatti = Number(riga.km_fatti) || 0;
            const kmNoleggio = Number(riga.km_noleggio) || 0;
            const chiavePeriodo = `${riga.anno}-${String(riga.mese).padStart(2, '0')}`;

            // Livello 1
            if (!raggruppati[valore1]) {
                raggruppati[valore1] = { id: valore1, label: valore1, periodi: {}, children: [], childrenMap: {} };
            }

            const accumulaPeriodo = (target) => {
                if (!target.periodi[chiavePeriodo]) target.periodi[chiavePeriodo] = creaPeriodoVuoto();
                target.periodi[chiavePeriodo].kmFatti += kmFatti;
                target.periodi[chiavePeriodo].kmNoleggio += kmNoleggio;
                if (kmNoleggio > 0) target.periodi[chiavePeriodo].kmFattiContratto += kmFatti;
            };

            if (!campoLivello2) {
                accumulaPeriodo(raggruppati[valore1]);
            } else {
                if (!raggruppati[valore1].childrenMap[valore2]) {
                    raggruppati[valore1].childrenMap[valore2] = { id: `${valore1}-${valore2}`, label: valore2, periodi: {}, children: [], childrenMap: {} };
                }
                const figlio = raggruppati[valore1].childrenMap[valore2];

                if (!campoLivello3) {
                    accumulaPeriodo(figlio);
                } else {
                    if (!figlio.childrenMap[valore3]) {
                        figlio.childrenMap[valore3] = { id: `${valore1}-${valore2}-${valore3}`, label: valore3, periodi: {}, children: [] };
                    }
                    accumulaPeriodo(figlio.childrenMap[valore3]);
                }
            }
        }

        // Converto childrenMap in array e sommo periodi figli al padre
        const sommaPeriodiFigli = (padre) => {
            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];
                const periodiFiglio = Object.keys(figlio.periodi);
                for (let k = 0; k < periodiFiglio.length; k++) {
                    const chiave = periodiFiglio[k];
                    const dati = figlio.periodi[chiave];
                    if (!padre.periodi[chiave]) padre.periodi[chiave] = creaPeriodoVuoto();
                    padre.periodi[chiave].kmFatti += dati.kmFatti;
                    padre.periodi[chiave].kmNoleggio += dati.kmNoleggio;
                    padre.periodi[chiave].kmFattiContratto += dati.kmFattiContratto;
                }
            }
        };

        const padri = Object.keys(raggruppati);
        for (let i = 0; i < padri.length; i++) {
            const padre = raggruppati[padri[i]];
            padre.children = Object.values(padre.childrenMap || {});
            delete padre.childrenMap;

            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];
                figlio.children = Object.values(figlio.childrenMap || {});
                delete figlio.childrenMap;

                if (figlio.children.length > 0) sommaPeriodiFigli(figlio);
            }
            if (padre.children.length > 0) sommaPeriodiFigli(padre);
        }

        // Calcolo aggregati (trimestre, anno, totale)
        const calcolaAggregati = (elemento) => {
            const trimestri = {};
            const anni = {};
            let totale = creaPeriodoVuoto();

            const periodiMensili = Object.keys(elemento.periodi);
            for (let j = 0; j < periodiMensili.length; j++) {
                const chiave = periodiMensili[j];
                if (!chiave.match(/^\d{4}-\d{2}$/)) continue;
                const periodo = elemento.periodi[chiave];
                const parti = chiave.split('-');
                const anno = parti[0];
                const trimestre = meseToTrim[parti[1]];
                const chiaveTrimestre = `${anno}-${trimestre}`;

                if (!trimestri[chiaveTrimestre]) trimestri[chiaveTrimestre] = creaPeriodoVuoto();
                trimestri[chiaveTrimestre].kmFatti += periodo.kmFatti;
                trimestri[chiaveTrimestre].kmNoleggio += periodo.kmNoleggio;
                trimestri[chiaveTrimestre].kmFattiContratto += periodo.kmFattiContratto;

                if (!anni[anno]) anni[anno] = creaPeriodoVuoto();
                anni[anno].kmFatti += periodo.kmFatti;
                anni[anno].kmNoleggio += periodo.kmNoleggio;
                anni[anno].kmFattiContratto += periodo.kmFattiContratto;

                totale.kmFatti += periodo.kmFatti;
                totale.kmNoleggio += periodo.kmNoleggio;
                totale.kmFattiContratto += periodo.kmFattiContratto;
            }

            Object.keys(trimestri).forEach(k => { elemento.periodi[k] = trimestri[k]; });
            Object.keys(anni).forEach(k => { elemento.periodi[k] = anni[k]; });
            elemento.periodi['totale'] = totale;
        };

        // Applico aggregati dal basso verso l'alto
        const listaPadri = Object.keys(raggruppati);
        for (let i = 0; i < listaPadri.length; i++) {
            const padre = raggruppati[listaPadri[i]];
            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];
                for (let k = 0; k < figlio.children.length; k++) {
                    calcolaAggregati(figlio.children[k]);
                }
                calcolaAggregati(figlio);
            }
            calcolaAggregati(padre);
        }

        return Object.values(raggruppati);
    };

    // Dati filtrati: prima tabella senza Spazzatrice, seconda solo Spazzatrice
    const tableDataNoSpazz = useMemo(() => tableData.filter(d => d.tipologia !== 'Spazzatrice'), [tableData]);
    const tableDataSpazz = useMemo(() => tableData.filter(d => d.tipologia === 'Spazzatrice'), [tableData]);
    const tableDataTotaleNoSpazz = useMemo(() => tableDataTotale.filter(d => d.tipologia !== 'Spazzatrice'), [tableDataTotale]);
    const tableDataTotaleSpazz = useMemo(() => tableDataTotale.filter(d => d.tipologia === 'Spazzatrice'), [tableDataTotale]);

    const datiTrasformati = useMemo(() => trasformaDati(tableDataNoSpazz), [tableDataNoSpazz, selectedRighe]);
    const datiTrasformatiTotale = useMemo(() => trasformaDati(tableDataTotaleNoSpazz), [tableDataTotaleNoSpazz, selectedRighe]);
    const datiTrasformatiSpazz = useMemo(() => trasformaDati(tableDataSpazz), [tableDataSpazz, selectedRighe]);
    const datiTrasformatiTotaleSpazz = useMemo(() => trasformaDati(tableDataTotaleSpazz), [tableDataTotaleSpazz, selectedRighe]);

    // Mappa id -> periodi['totale'] dai dati non filtrati per date (totale contratto)
    const buildTotaliContrattoMap = (dati) => {
        const mappa = {};
        const costruisciMappa = (elementi) => {
            for (let i = 0; i < elementi.length; i++) {
                const el = elementi[i];
                if (el.periodi && el.periodi['totale']) {
                    mappa[el.id] = el.periodi['totale'];
                }
                if (el.children && el.children.length > 0) {
                    costruisciMappa(el.children);
                }
            }
        };
        costruisciMappa(dati);
        return mappa;
    };
    const totaliContrattoMap = useMemo(() => buildTotaliContrattoMap(datiTrasformatiTotale), [datiTrasformatiTotale]);
    const totaliContrattoMapSpazz = useMemo(() => buildTotaliContrattoMap(datiTrasformatiTotaleSpazz), [datiTrasformatiTotaleSpazz]);

    // Totali per periodo (per 'totale' usa i dati non filtrati per date)
    const buildTotaliPerPeriodo = (datiFiltered, datiTotaleFiltered) => {
        const totali = {};
        for (let i = 0; i < periodiDaMostrare.length; i++) {
            const p = periodiDaMostrare[i];
            totali[p.chiave] = { kmFatti: 0, kmNoleggio: 0, kmFattiContratto: 0 };
        }
        for (let i = 0; i < datiFiltered.length; i++) {
            const riga = datiFiltered[i];
            for (let j = 0; j < periodiDaMostrare.length; j++) {
                const p = periodiDaMostrare[j];
                if (p.chiave === 'totale') continue;
                if (riga.periodi[p.chiave]) {
                    totali[p.chiave].kmFatti += riga.periodi[p.chiave].kmFatti;
                    totali[p.chiave].kmNoleggio += riga.periodi[p.chiave].kmNoleggio;
                    totali[p.chiave].kmFattiContratto += riga.periodi[p.chiave].kmFattiContratto;
                }
            }
        }
        if (totali['totale'] !== undefined) {
            totali['totale'] = { kmFatti: 0, kmNoleggio: 0, kmFattiContratto: 0 };
            for (let i = 0; i < datiTotaleFiltered.length; i++) {
                const riga = datiTotaleFiltered[i];
                if (riga.periodi['totale']) {
                    totali['totale'].kmFatti += riga.periodi['totale'].kmFatti;
                    totali['totale'].kmNoleggio += riga.periodi['totale'].kmNoleggio;
                    totali['totale'].kmFattiContratto += riga.periodi['totale'].kmFattiContratto;
                }
            }
        }
        return totali;
    };
    const totaliPerPeriodo = useMemo(() => buildTotaliPerPeriodo(datiTrasformati, datiTrasformatiTotale), [datiTrasformati, datiTrasformatiTotale, periodiDaMostrare]);
    const totaliPerPeriodoSpazz = useMemo(() => buildTotaliPerPeriodo(datiTrasformatiSpazz, datiTrasformatiTotaleSpazz), [datiTrasformatiSpazz, datiTrasformatiTotaleSpazz, periodiDaMostrare]);

    const colonnePerPeriodo = 3; // Km Fatti, Km Noleggio, % Utilizzo

    const toggleRowExpansion = (rowKey) => {
        setExpandedRows(prev => prev.includes(rowKey) ? prev.filter(id => id !== rowKey) : [...prev, rowKey]);
    };
    const isRowExpanded = (rowKey) => expandedRows.includes(rowKey);

    const haAnnoSelezionato = selectedColonne.some(opt => opt.value === 'anno');
    const haTrimestreSelezionato = selectedColonne.some(opt => opt.value === 'trimestre');
    const haMeseSelezionato = selectedColonne.some(opt => opt.value === 'mese');

    // Export Excel
    const handleDownloadFile = async (datiDaEsportare) => {
        try {
            const response = await axios.post(`${API_URL}/operation-automezzi/downloadExcel`,
                { tableName: 'Status Noleggi', datiDaStampare: datiDaEsportare },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "status_noleggi.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);
        }
    };

    const prepareExportDataGeneric = (dati, totPeriodo, contrattoMap) => {
        if (!dati || dati.length === 0) return null;

        // Riga raggruppamento periodi (per merge celle)
        const headerGroups = [];
        for (let i = 0; i < periodiDaMostrare.length; i++) {
            const p = periodiDaMostrare[i];
            const label = p.anno && p.anno !== 'totale' && p.etichetta !== p.anno
                ? `${p.etichetta} ${p.anno}`
                : p.etichetta;
            headerGroups.push({ label, span: 3 });
        }

        // Sotto-header ripetuti per ogni periodo
        const headers = [''];
        for (let i = 0; i < periodiDaMostrare.length; i++) {
            headers.push('Km Fatti');
            headers.push('Km Noleggio');
            headers.push('% Utilizzo');
        }

        const rows = [];
        const aggiungiRiga = (label, periodi, rowId) => {
            const riga = [label];
            for (let i = 0; i < periodiDaMostrare.length; i++) {
                const p = periodiDaMostrare[i];
                const usaTotaleContratto = p.chiave === 'totale' && rowId && contrattoMap[rowId];
                const d = usaTotaleContratto ? contrattoMap[rowId] : (periodi[p.chiave] || { kmFatti: 0, kmNoleggio: 0, kmFattiContratto: 0 });
                const perc = d.kmNoleggio > 0 ? Math.round(d.kmFattiContratto / d.kmNoleggio * 1000) / 10 : 0;
                riga.push(d.kmFatti);
                riga.push(d.kmNoleggio);
                riga.push(perc);
            }
            rows.push(riga);
        };

        for (let i = 0; i < dati.length; i++) {
            const padre = dati[i];
            aggiungiRiga(padre.label, padre.periodi, padre.id);
            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];
                aggiungiRiga('  ' + figlio.label, figlio.periodi, figlio.id);
                for (let k = 0; k < figlio.children.length; k++) {
                    aggiungiRiga('    ' + figlio.children[k].label, figlio.children[k].periodi, figlio.children[k].id);
                }
            }
        }

        aggiungiRiga('TOTALE', totPeriodo);
        return { headerGroups, headers, rows };
    };

    const prepareExportData = () => prepareExportDataGeneric(datiTrasformati, totaliPerPeriodo, totaliContrattoMap);
    const prepareExportDataSpazz = () => prepareExportDataGeneric(datiTrasformatiSpazz, totaliPerPeriodoSpazz, totaliContrattoMapSpazz);

    // Helper per renderizzare le celle di una riga
    const getUtilizzoColor = (perc) => {
        if (perc < 100) return '#10B981';
        return '#EF4444';
    };

    const renderUtilizzoCell = (percUtilizzo) => {
        if (percUtilizzo === 0) return <td>-</td>;
        const capped = Math.min(percUtilizzo, 100);
        const color = getUtilizzoColor(percUtilizzo);
        return (
            <td className="td-utilizzo">
                <div className="sn-utilizzo-wrapper">
                    <div className="sn-utilizzo-bar-bg">
                        <div className="sn-utilizzo-bar-fill" style={{ width: `${capped}%`, background: color }}></div>
                    </div>
                    <span className="sn-utilizzo-value" style={{ color }}>{formatNumber(percUtilizzo)}%</span>
                </div>
            </td>
        );
    };

    const renderCelleRiga = (periodi, rowId, contrattoMap = totaliContrattoMap) => {
        return periodiDaMostrare.map((periodo) => {
            const usaTotaleContratto = periodo.chiave === 'totale' && rowId && contrattoMap[rowId];
            const d = usaTotaleContratto ? contrattoMap[rowId] : (periodi[periodo.chiave] || { kmFatti: 0, kmNoleggio: 0, kmFattiContratto: 0 });
            const percUtilizzo = d.kmNoleggio > 0 ? (d.kmFattiContratto / d.kmNoleggio * 100) : 0;
            return (
                <React.Fragment key={periodo.chiave}>
                    <td>{formatNumber(d.kmFatti)}</td>
                    <td>{formatNumber(d.kmNoleggio)}</td>
                    {renderUtilizzoCell(percUtilizzo)}
                </React.Fragment>
            );
        });
    };

    // Helper per renderizzare una riga con expand
    const renderRiga = (row, livello, contrattoMap = totaliContrattoMap) => {
        const haFigli = row.children && row.children.length > 0;
        const rigaAperta = isRowExpanded(row.id);
        let icona = '•';
        if (haFigli) icona = rigaAperta ? '▼' : '▶';
        const classeSettore = `td-settore td-indent-${livello}${haFigli ? ' td-expandable' : ''}`;

        return (
            <React.Fragment key={row.id}>
                <tr className={`tr-level-${livello + 1}`}>
                    <td className={classeSettore} onClick={() => haFigli && toggleRowExpansion(row.id)}>
                        <span className="expand-icon">{icona}</span>
                        <span className="level-value">{row.label}</span>
                    </td>
                    {renderCelleRiga(row.periodi, row.id, contrattoMap)}
                </tr>
                {rigaAperta && row.children && row.children.map(figlio => renderRiga(figlio, livello + 1, contrattoMap))}
            </React.Fragment>
        );
    };

    // =============================================
    // Configurazione grafico combo
    // =============================================
    const comboChartOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            formatter: (params) => {
                let result = params[0].axisValue + '<br/>';
                params.forEach(p => {
                    if (p.seriesName === '% Utilizzo') {
                        result += `<span style="color:${p.color}">●</span> ${p.seriesName}: ${p.value}%<br/>`;
                    } else {
                        result += `<span style="color:${p.color}">●</span> ${p.seriesName}: ${Number(p.value).toLocaleString('it-IT')} km<br/>`;
                    }
                });
                return result;
            }
        },
        legend: { data: ['Km Fatti', 'Km Noleggio', '% Utilizzo'], top: 0, left: 0 },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: chartData.map(item => item.sottotipologia), axisLabel: { rotate: 30 } },
        yAxis: [
            { type: 'value', name: 'Km', position: 'left', axisLabel: { formatter: (val) => val >= 1000000 ? (val / 1000000).toFixed(1) + 'M' : val >= 1000 ? (val / 1000).toFixed(0) + 'K' : val } },
            { type: 'value', name: '%', position: 'right', min: 0, max: 100, axisLabel: { formatter: '{value}%' } }
        ],
        series: [
            {
                name: 'Km Fatti', type: 'bar', data: chartData.map(item => Number(item.km_fatti)),
                itemStyle: { color: '#EF4444' }, barGap: '0%',
                label: { show: true, position: 'top', formatter: (p) => p.value >= 1000000 ? (p.value / 1000000).toFixed(1) + 'M' : p.value >= 1000 ? (p.value / 1000).toFixed(0) + 'K' : p.value, fontSize: 10, color: '#EF4444', fontWeight: 'bold' }
            },
            {
                name: 'Km Noleggio', type: 'bar', data: chartData.map(item => Number(item.km_noleggio)),
                itemStyle: { color: '#91cc75' },
                label: { show: true, position: 'top', formatter: (p) => p.value >= 1000000 ? (p.value / 1000000).toFixed(1) + 'M' : p.value >= 1000 ? (p.value / 1000).toFixed(0) + 'K' : p.value, fontSize: 10, color: '#91cc75', fontWeight: 'bold' }
            },
            {
                name: '% Utilizzo', type: 'line', yAxisIndex: 1,
                data: chartData.map(item => { const val = Number(item.perc_utilizzo); return val > 100 ? 100 : val; }),
                itemStyle: { color: '#F59E0B' }, lineStyle: { width: 3, type: 'dashed' }, symbolSize: 10,
                label: { show: true, position: 'top', formatter: (p) => Number(chartData[p.dataIndex]?.perc_utilizzo || 0).toFixed(1) + '%', color: '#F59E0B', fontWeight: 'bold' }
            }
        ]
    };

    return (
        <div className="status-noleggi-container">
            {isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="oper-hr-empty-state">
                    <div className="oper-hr-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                    <p className="oper-hr-empty-text">Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.</p>
                </div>
            ) : isDataReady ? (
                <>
                    {/* KPI Cards */}
                    <div className="status-noleggi-kpi-cards">
                        <div className="status-noleggi-kpi-card kpi-purple">
                            <div className="status-noleggi-kpi-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="1" y="3" width="15" height="13" rx="2" ry="2"></rect>
                                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                </svg>
                            </div>
                            <div className="status-noleggi-kpi-content">
                                <span className="status-noleggi-kpi-value"><AnimatedCounter value={Number(kpiData.veicoli_noleggiati)} /></span>
                                <span className="status-noleggi-kpi-label">Veicoli Noleggiati</span>
                            </div>
                        </div>
                        <div className="status-noleggi-kpi-card kpi-green">
                            <div className="status-noleggi-kpi-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                </svg>
                            </div>
                            <div className="status-noleggi-kpi-content">
                                <span className="status-noleggi-kpi-value"><AnimatedCounter value={Number(kpiData.km_totali_percorsi)} /></span>
                                <span className="status-noleggi-kpi-label">Km Totali Percorsi</span>
                            </div>
                        </div>
                        <div className="status-noleggi-kpi-card kpi-blue">
                            <div className="status-noleggi-kpi-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                </svg>
                            </div>
                            <div className="status-noleggi-kpi-content">
                                <span className="status-noleggi-kpi-value"><AnimatedCounter value={Number(kpiData.km_disponibili_noleggio)} /></span>
                                <span className="status-noleggi-kpi-label">Km Disponibili Noleggio</span>
                            </div>
                        </div>
                        <div className="status-noleggi-kpi-card kpi-orange">
                            <div className="status-noleggi-kpi-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                            </div>
                            <div className="status-noleggi-kpi-content">
                                <span className="status-noleggi-kpi-value"><AnimatedCounter value={Number(kpiData.perc_utilizzo_media)} decimals={1} suffix="%" /></span>
                                <span className="status-noleggi-kpi-label">% Utilizzo Media</span>
                            </div>
                        </div>
                    </div>

                    {/* Grafico combo */}
                    <div className={`status-noleggi-chart-wrapper${isFullscreenChart ? ' fullscreen-chart' : ''}`}>
                        <RotateDevice active={isFullscreenChart} />
                        <div className="status-noleggi-chart-header">
                            <span className="charts-title">Km Fatti vs Km Noleggio per Sottotipologia</span>
                            <button className="automezzi-gen-fullscreen-btn" onClick={() => setIsFullscreenChart(!isFullscreenChart)} title={isFullscreenChart ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenChart ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                                </svg>
                            </button>
                        </div>
                        <ReactECharts option={comboChartOption} style={{ height: '400px', width: '100%' }} notMerge={true} />
                    </div>

                    {/* Tabella Parametrica Km Noleggio*/}
                    <div className={`sn-table-section${isFullScreenTable ? ' fullscreen-table' : ''}`}>
                        <RotateDevice active={isFullScreenTable} />

                        <div className="sn-table-header">
                            <span className="sn-table-title">Dettaglio Km Noleggi</span>
                            <div className="sn-table-header-btn">
                                <button className="sn-download-btn" onClick={() => handleDownloadFile(prepareExportData())} disabled={datiTrasformati.length === 0}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>
                                <button className="sn-fullscreen-btn" onClick={() => setIsFullScreenTable(!isFullScreenTable)} title={isFullScreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullScreenTable ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Config row: solo COLONNE e RIGHE */}
                        <div className="sn-config-row">
                            <div className="sn-config-item">
                                <label>COLONNE</label>
                                <Select classNamePrefix="sn-select" placeholder="Selezioni multiple" value={selectedColonne} options={colonneOptions} onChange={setSelectedColonne} isMulti closeMenuOnSelect={false} hideSelectedOptions={false} components={{ MultiValue: MultiValueContainer }} />
                            </div>
                            <div className="sn-config-item">
                                <label>RIGHE</label>
                                <Select classNamePrefix="sn-select" placeholder="Selezioni multiple" value={selectedRighe} options={righeOptions} onChange={setSelectedRighe} isMulti closeMenuOnSelect={false} hideSelectedOptions={false} components={{ MultiValue: MultiValueContainer }} />
                            </div>
                        </div>

                        <div className="sn-table-wrapper">
                            {tableLoading ? (
                                <div className="sn-loading-overlay">
                                    <div className="sn-spinner"></div>
                                    <span>Caricamento...</span>
                                </div>
                            ) : (
                                <table className="sn-table">
                                    <thead>
                                        {/* Riga Anno */}
                                        {haAnnoSelezionato && (
                                            <tr>
                                                <th className="th-settore">Anno</th>
                                                {(() => {
                                                    let celle = [];
                                                    for (let i = 0; i < anniDisponibili.length; i++) {
                                                        const anno = anniDisponibili[i];
                                                        let colSpan = colonnePerPeriodo;
                                                        if (haMeseSelezionato) {
                                                            let mesiValidi = 0;
                                                            for (let m = 1; m <= 12; m++) { if (isMeseValido(anno, m)) mesiValidi++; }
                                                            colSpan = mesiValidi * colonnePerPeriodo;
                                                        } else if (haTrimestreSelezionato) {
                                                            let trimValidi = 0;
                                                            for (let t = 1; t <= 4; t++) { if (isTrimestreValido(anno, t)) trimValidi++; }
                                                            colSpan = trimValidi * colonnePerPeriodo;
                                                        }
                                                        if (colSpan > 0) celle.push(<th key={anno} colSpan={colSpan} className="th-periodo">{anno}</th>);
                                                    }
                                                    let rowSpanTotale = 1;
                                                    if (haTrimestreSelezionato) rowSpanTotale++;
                                                    if (haMeseSelezionato) rowSpanTotale++;
                                                    celle.push(<th key="totale" colSpan={colonnePerPeriodo} rowSpan={rowSpanTotale} className="th-periodo th-totale-header">Totale</th>);
                                                    return celle;
                                                })()}
                                            </tr>
                                        )}

                                        {/* Riga Trimestre */}
                                        {haTrimestreSelezionato && (
                                            <tr>
                                                <th className="th-settore">Trimestre</th>
                                                {(() => {
                                                    let celle = [];
                                                    const mesiPerTrimestre = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };
                                                    for (let i = 0; i < anniDisponibili.length; i++) {
                                                        const anno = anniDisponibili[i];
                                                        for (let t = 1; t <= 4; t++) {
                                                            if (isTrimestreValido(anno, t)) {
                                                                let colSpan = colonnePerPeriodo;
                                                                if (haMeseSelezionato) {
                                                                    colSpan = mesiPerTrimestre[t].filter(m => isMeseValido(anno, m)).length * colonnePerPeriodo;
                                                                }
                                                                celle.push(<th key={`${anno}-t${t}`} colSpan={colSpan} className="th-periodo">Q{t} {anno}</th>);
                                                            }
                                                        }
                                                    }
                                                    if (!haAnnoSelezionato) {
                                                        let rowSpan = 1;
                                                        if (haMeseSelezionato) rowSpan++;
                                                        celle.push(<th key="totale" colSpan={colonnePerPeriodo} rowSpan={rowSpan} className="th-periodo th-totale-header">Totale</th>);
                                                    }
                                                    return celle;
                                                })()}
                                            </tr>
                                        )}

                                        {/* Riga Mese */}
                                        {haMeseSelezionato && (
                                            <tr>
                                                <th className="th-settore">Mese</th>
                                                {(() => {
                                                    let celle = [];
                                                    for (let i = 0; i < anniDisponibili.length; i++) {
                                                        const anno = anniDisponibili[i];
                                                        for (let m = 0; m < 12; m++) {
                                                            if (isMeseValido(anno, m + 1)) {
                                                                celle.push(<th key={`${anno}-${m}`} colSpan={colonnePerPeriodo} className="th-periodo">{nomiMesi[m]} {anno}</th>);
                                                            }
                                                        }
                                                    }
                                                    if (!haAnnoSelezionato && !haTrimestreSelezionato) {
                                                        celle.push(<th key="totale" colSpan={colonnePerPeriodo} className="th-periodo th-totale-header">Totale</th>);
                                                    }
                                                    return celle;
                                                })()}
                                            </tr>
                                        )}

                                        {/* Riga indicatori (sempre visibile) */}
                                        <tr>
                                            <th className="th-settore">Raggruppamento</th>
                                            {periodiDaMostrare.map((periodo) => {
                                                const cls = periodo.chiave === 'totale' ? 'th-metrica th-metrica-totale' : 'th-metrica';
                                                return (
                                                    <React.Fragment key={periodo.chiave}>
                                                        <th className={cls}>Km Fatti</th>
                                                        <th className={cls}>Km Noleggio</th>
                                                        <th className={cls}>% Utilizzo</th>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {datiTrasformati.map((row) => renderRiga(row, 0))}

                                        {/* Riga totali */}
                                        <tr className="tr-totali">
                                            <td className="td-settore" style={{ fontWeight: 700 }}>TOTALE</td>
                                            {periodiDaMostrare.map((periodo) => {
                                                const d = totaliPerPeriodo[periodo.chiave] || { kmFatti: 0, kmNoleggio: 0, kmFattiContratto: 0 };
                                                const percUtilizzo = d.kmNoleggio > 0 ? (d.kmFattiContratto / d.kmNoleggio * 100) : 0;
                                                return (
                                                    <React.Fragment key={periodo.chiave}>
                                                        <td>{formatNumber(d.kmFatti)}</td>
                                                        <td>{formatNumber(d.kmNoleggio)}</td>
                                                        {renderUtilizzoCell(percUtilizzo)}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Tabella Parametrica Ore Noleggi*/}
                    <div className={`sn-table-section${isFullScreenTable ? ' fullscreen-table' : ''}`}>
                        <RotateDevice active={isFullScreenTable} />

                        <div className="sn-table-header">
                            <span className="sn-table-title">Dettaglio Ore Noleggi Spazzatrice</span>
                            <div className="sn-table-header-btn">
                                <button className="sn-download-btn" onClick={() => handleDownloadFile(prepareExportDataSpazz())} disabled={datiTrasformatiSpazz.length === 0}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>
                                <button className="sn-fullscreen-btn" onClick={() => setIsFullScreenTable(!isFullScreenTable)} title={isFullScreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullScreenTable ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Config row: solo COLONNE e RIGHE */}
                        <div className="sn-config-row">
                            <div className="sn-config-item">
                                <label>COLONNE</label>
                                <Select classNamePrefix="sn-select" placeholder="Selezioni multiple" value={selectedColonne} options={colonneOptions} onChange={setSelectedColonne} isMulti closeMenuOnSelect={false} hideSelectedOptions={false} components={{ MultiValue: MultiValueContainer }} />
                            </div>
                            <div className="sn-config-item">
                                <label>RIGHE</label>
                                <Select classNamePrefix="sn-select" placeholder="Selezioni multiple" value={selectedRighe} options={righeOptions} onChange={setSelectedRighe} isMulti closeMenuOnSelect={false} hideSelectedOptions={false} components={{ MultiValue: MultiValueContainer }} />
                            </div>
                        </div>

                        <div className="sn-table-wrapper">
                            {tableLoading ? (
                                <div className="sn-loading-overlay">
                                    <div className="sn-spinner"></div>
                                    <span>Caricamento...</span>
                                </div>
                            ) : (
                                <table className="sn-table">
                                    <thead>
                                        {/* Riga Anno */}
                                        {haAnnoSelezionato && (
                                            <tr>
                                                <th className="th-settore">Anno</th>
                                                {(() => {
                                                    let celle = [];
                                                    for (let i = 0; i < anniDisponibili.length; i++) {
                                                        const anno = anniDisponibili[i];
                                                        let colSpan = colonnePerPeriodo;
                                                        if (haMeseSelezionato) {
                                                            let mesiValidi = 0;
                                                            for (let m = 1; m <= 12; m++) { if (isMeseValido(anno, m)) mesiValidi++; }
                                                            colSpan = mesiValidi * colonnePerPeriodo;
                                                        } else if (haTrimestreSelezionato) {
                                                            let trimValidi = 0;
                                                            for (let t = 1; t <= 4; t++) { if (isTrimestreValido(anno, t)) trimValidi++; }
                                                            colSpan = trimValidi * colonnePerPeriodo;
                                                        }
                                                        if (colSpan > 0) celle.push(<th key={anno} colSpan={colSpan} className="th-periodo">{anno}</th>);
                                                    }
                                                    let rowSpanTotale = 1;
                                                    if (haTrimestreSelezionato) rowSpanTotale++;
                                                    if (haMeseSelezionato) rowSpanTotale++;
                                                    celle.push(<th key="totale" colSpan={colonnePerPeriodo} rowSpan={rowSpanTotale} className="th-periodo th-totale-header">Totale</th>);
                                                    return celle;
                                                })()}
                                            </tr>
                                        )}

                                        {/* Riga Trimestre */}
                                        {haTrimestreSelezionato && (
                                            <tr>
                                                <th className="th-settore">Trimestre</th>
                                                {(() => {
                                                    let celle = [];
                                                    const mesiPerTrimestre = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };
                                                    for (let i = 0; i < anniDisponibili.length; i++) {
                                                        const anno = anniDisponibili[i];
                                                        for (let t = 1; t <= 4; t++) {
                                                            if (isTrimestreValido(anno, t)) {
                                                                let colSpan = colonnePerPeriodo;
                                                                if (haMeseSelezionato) {
                                                                    colSpan = mesiPerTrimestre[t].filter(m => isMeseValido(anno, m)).length * colonnePerPeriodo;
                                                                }
                                                                celle.push(<th key={`${anno}-t${t}`} colSpan={colSpan} className="th-periodo">Q{t} {anno}</th>);
                                                            }
                                                        }
                                                    }
                                                    if (!haAnnoSelezionato) {
                                                        let rowSpan = 1;
                                                        if (haMeseSelezionato) rowSpan++;
                                                        celle.push(<th key="totale" colSpan={colonnePerPeriodo} rowSpan={rowSpan} className="th-periodo th-totale-header">Totale</th>);
                                                    }
                                                    return celle;
                                                })()}
                                            </tr>
                                        )}

                                        {/* Riga Mese */}
                                        {haMeseSelezionato && (
                                            <tr>
                                                <th className="th-settore">Mese</th>
                                                {(() => {
                                                    let celle = [];
                                                    for (let i = 0; i < anniDisponibili.length; i++) {
                                                        const anno = anniDisponibili[i];
                                                        for (let m = 0; m < 12; m++) {
                                                            if (isMeseValido(anno, m + 1)) {
                                                                celle.push(<th key={`${anno}-${m}`} colSpan={colonnePerPeriodo} className="th-periodo">{nomiMesi[m]} {anno}</th>);
                                                            }
                                                        }
                                                    }
                                                    if (!haAnnoSelezionato && !haTrimestreSelezionato) {
                                                        celle.push(<th key="totale" colSpan={colonnePerPeriodo} className="th-periodo th-totale-header">Totale</th>);
                                                    }
                                                    return celle;
                                                })()}
                                            </tr>
                                        )}

                                        {/* Riga indicatori (sempre visibile) */}
                                        <tr>
                                            <th className="th-settore">Raggruppamento</th>
                                            {periodiDaMostrare.map((periodo) => {
                                                const cls = periodo.chiave === 'totale' ? 'th-metrica th-metrica-totale' : 'th-metrica';
                                                return (
                                                    <React.Fragment key={periodo.chiave}>
                                                        <th className={cls}>Ore Fatte</th>
                                                        <th className={cls}>Ore Noleggio</th>
                                                        <th className={cls}>% Utilizzo</th>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {datiTrasformatiSpazz.map((row) => renderRiga(row, 0, totaliContrattoMapSpazz))}

                                        {/* Riga totali */}
                                        <tr className="tr-totali">
                                            <td className="td-settore" style={{ fontWeight: 700 }}>TOTALE</td>
                                            {periodiDaMostrare.map((periodo) => {
                                                const d = totaliPerPeriodoSpazz[periodo.chiave] || { kmFatti: 0, kmNoleggio: 0, kmFattiContratto: 0 };
                                                const percUtilizzo = d.kmNoleggio > 0 ? (d.kmFattiContratto / d.kmNoleggio * 100) : 0;
                                                return (
                                                    <React.Fragment key={periodo.chiave}>
                                                        <td>{formatNumber(d.kmFatti)}</td>
                                                        <td>{formatNumber(d.kmNoleggio)}</td>
                                                        {renderUtilizzoCell(percUtilizzo)}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default StatusNoleggi;
